import Config from '@/App/Config/index.vue'
import Main from '@/App/Main/index.vue'
import { store } from "@/store"
import { utils } from '@hscmap/stellar-globe'
import VueRouter from "vue-router"
import { CatalogOverplot } from '@/modules/CatalogOverplotModule/CatalogOverplot'
import { defaultParams, Params } from '@/modules/SspDataModule'

const router = new VueRouter({
  routes: [
    { name: 'main', path: '/', component: Main },
    { name: 'config', path: '/config', component: Config },
  ],
})

router.afterEach((to, from) => {
  if (from.name !== 'main' && to.name === 'main') {
    safeJsonParse(to.query._ as string, loadParams)
  }
})

const routerSync = utils.debounce(400, () => {
  router.push({
    name: 'main',
    query: {
      _: JSON.stringify(routeParams()),
    },
  }, () => { })
})

type CatalogDef = {
  name: string
  csv: string
}

export type ExternalTileDataDef = {
  baseUrl: string
  colorParams?: Params
}

type RouterParams = ReturnType<typeof routeParams>
type RouterParamsIn = Partial<RouterParams> & {
  catalogs?: CatalogDef[]
}

function loadParams(params: RouterParamsIn) {
  const frame = store.currentFrame
  params.view && (frame.view = params.view)
  params.sspParams && (frame.modules.sspData.params = convertSspParams(params.sspParams))
  if (params.externalTiles) {
    for (const t of params.externalTiles) {
      store.modules.externalTileData.add(t)
    }
  }
  (async () => {
    if (params.catalogs) {
      for (const { name, csv } of params.catalogs) {
        const co = await CatalogOverplot.fromCsvArrayBuffer(name, (new TextEncoder().encode(csv)))
        frame.modules.catalogOverPlot.cos.push(co)
        store.modules.catalogOverPlot.showList = true
      }
    }
  })()
  if (params.activeReruns) {
    store.currentFrame.modules.sspData.activeReruns = params.activeReruns
  }
}

function routeParams() {
  return {
    view: store.currentFrame.view,
    sspParams: store.currentFrame.modules.sspData.params,
    externalTiles: store.modules.externalTileData.entries.map(t => ({ baseUrl: t.baseUrl, colorParams: t.colorParams })),
    activeReruns: store.currentFrame.modules.sspData.activeReruns,
  }
}

function convertSspParams(params: any): RouterParams["sspParams"] {
  if (params.mixerParams) { // convert old format
    const { mixerParams } = params
    const { simpleRgb, sdssTrueColor, simpleColorMatrix, type } = mixerParams
    const filter = mixerParams[({ SDSS_TRUE_COLOR: 'sdssTrueColor', SIMPLE_RGB: 'simpleRgb' } as any)[type]].filter
    return {
      type,
      filter,
      sdssTrueColor: objectSlice(sdssTrueColor, ['beta', 'bias', 'a', 'b0']),
      simpleRgb: objectSlice(simpleRgb, ['beta', 'bias', 'a', 'b0']),
      simpleColorMatrix: defaultParams().simpleColorMatrix,
    }
  }
  else {
    params = { ...defaultParams(), ...params }
  }
  return params
}

function safeJsonParse<T>(json: string, cb: (value: any) => T) {
  if (json) {
    let value: any
    try {
      value = JSON.parse(json)
      return cb(value)
    } catch (e) {
      console.error(e)
    }
  }
}

function objectSlice(o: any, keys: string[]) {
  const d = {} as any
  for (const k of keys) { d[k] = o[k] }
  return d
}

export { router, routerSync }
